import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  InvitationBatchResponseV1,
  InvitationResponseV1,
  InvitationsSummaryV1,
  InviteUsersToGroupRequestV1
} from '@access-management/invitation';
import { catchError, map, Observable, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvitationService {
  private readonly http = inject(HttpClient);
  private readonly invitationsApiUrl = `${environment.apiUrl}${environment.apis.iamEngine}/v1/invitations`;
  private readonly usersApiUrl = `${environment.apiUrl}${environment.apis.iamEngine}/v1/users`;
  private readonly groupsApiUrl = `${environment.apiUrl}${environment.apis.iamEngine}/v1/groups`;
  private readonly adminStatsApiUrl = `${environment.apiUrl}${environment.apis.iamEngine}/v1/stats`;
  private readonly httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  private handleResourceError<T>(err: unknown): Observable<never> {
    let error: Error;
    if (err instanceof HttpErrorResponse) {
      error = new Error(`HTTP ${err.status}: ${err.message}`);
    } else if (err instanceof Error) {
      error = err;
    } else {
      error = new Error(String(err));
    }
    return throwError(() => error);
  }

  createInvitationsResource() {
    return rxResource<InvitationResponseV1[], void>({
      stream: () =>
        this.http.get<ServiceResponseV1<InvitationResponseV1[]>>(this.invitationsApiUrl).pipe(
          map((response) => response.data || []),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  createInvitationsSummaryResource(topInviters = 10, topGroups = 50) {
    const url = `${this.adminStatsApiUrl}/invitations?topInviters=${topInviters}&topGroups=${topGroups}`;
    return rxResource<InvitationsSummaryV1 | null, void>({
      stream: () =>
        this.http.get<ServiceResponseV1<InvitationsSummaryV1>>(url).pipe(
          map((response) => response.data ?? null),
          catchError((err) => this.handleResourceError(err))
        )
    });
  }

  getGroupInvitations(groupId: string): Observable<InvitationResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<InvitationResponseV1[]>>(`${this.groupsApiUrl}/${groupId}/invitations`)
      .pipe(map((response) => response.data || []));
  }

  getInvitationsForInviter(userId: string): Observable<InvitationResponseV1[]> {
    return this.http
      .get<ServiceResponseV1<InvitationResponseV1[]>>(`${this.usersApiUrl}/${userId}/invitations`)
      .pipe(map((response) => response.data || []));
  }

  /**
   * Stages invitations for a group's next checkout. This is the only way a member joins one: there is no
   * add-member route, by design, because membership is established by the person accepting rather than by
   * staff asserting it.
   *
   * Nothing is delivered here. A household seat is purchased against the invitee's email address, and it is
   * that payment which issues the invitation's token and sends it. Delivering one first would produce an
   * invitation acceptance has no membership to allocate.
   *
   * The inviter has to be an existing member of the group, which the engine checks, so support staff pass a
   * member's id (the owner, normally) rather than their own.
   *
   * Returns the whole batch result, successes and refusals both, because the engine reports per-invitee
   * outcomes in a 200 body.
   */
  stageInvitationsForCheckout(
    groupId: string,
    request: InviteUsersToGroupRequestV1
  ): Observable<InvitationBatchResponseV1> {
    return this.http
      .put<
        ServiceResponseV1<InvitationBatchResponseV1>
      >(`${this.groupsApiUrl}/${groupId}/invite/staged`, request, this.httpOptions)
      .pipe(
        map((response) => {
          // An absent payload is an unknown outcome, not an empty one. Substituting an empty batch
          // reads downstream as "no refusals", which is indistinguishable from a successful send.
          const batch = response.data;
          if (!batch || !Array.isArray(batch.successes) || !Array.isArray(batch.errors)) {
            throw new Error('The invitation service returned no batch result.');
          }
          return batch;
        })
      );
  }

  resendInvitation(inviterUserId: string, invitationId: string): Observable<InvitationResponseV1> {
    const url = `${this.usersApiUrl}/${inviterUserId}/invitations/resend(${invitationId})`;
    return this.http
      .post<ServiceResponseV1<InvitationResponseV1>>(url, {}, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  updateInvitationEmail(inviterUserId: string, invitationId: string, newEmail: string): Observable<InvitationResponseV1> {
    const url = `${this.usersApiUrl}/${inviterUserId}/invitations/${invitationId}/email`;
    return this.http
      .put<ServiceResponseV1<InvitationResponseV1>>(url, { newEmail }, this.httpOptions)
      .pipe(map((response) => response.data!));
  }

  cancelInvitation(inviterUserId: string, invitationId: string): Observable<InvitationResponseV1> {
    const url = `${this.usersApiUrl}/${inviterUserId}/invitations/${invitationId}`;
    return this.http
      .delete<ServiceResponseV1<InvitationResponseV1>>(url)
      .pipe(map((response) => response.data!));
  }
}
